import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import type { DateValueType } from "react-tailwindcss-datepicker";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import type { BackgroundCheck } from "@/types";
import { EDIT_BACKGROUND_CHECK_LABEL } from "@/features/person/components/BackgroundCheckOrders/backgroundCheckCopy";
import { GET_PROSPECT_PROGRAM_QUERY } from "@/pages/people/[pgaId]/person/queries";
import { UPSERT_PERSON_BACKGROUND_CHECK_MUTATION, type BackgroundCheckDispositionStatus } from "./mutations";
import { showSnackbar } from "@/lib/snackbarUtils";
import {
  addOneYearIsoDate,
  isExpiredDisplayStatus,
  isFutureIsoDate,
  requiresDispositionConfirm,
  toScreenedOnPrefill,
  toWritableStatus,
  WRITABLE_STATUS_OPTIONS,
  type BackgroundCheckDispositionFormValues
} from "./utils";

function formatDateForDateSelect(dateString: string): DateValueType | string {
  if (!dateString) return "";
  return { startDate: dateString, endDate: dateString };
}

function dateSelectToIso(value: DateValueType | string | null): string {
  if (!value) return "";
  if (typeof value === "string") {
    return toScreenedOnPrefill(value);
  }
  const start = value.startDate;
  if (!start) return "";
  if (typeof start === "string") return toScreenedOnPrefill(start);
  return start.toISOString().slice(0, 10);
}

export type BackgroundCheckDispositionModalProps = {
  open: boolean;
  onClose: () => void;
  pgaId: string;
  backgroundCheck: BackgroundCheck | null | undefined;
};

export default function BackgroundCheckDispositionModal({
  open,
  onClose,
  pgaId,
  backgroundCheck
}: BackgroundCheckDispositionModalProps) {
  const [bannerError, setBannerError] = useState("");
  const [confirmStatus, setConfirmStatus] = useState<BackgroundCheckDispositionStatus | null>(null);

  const defaultValues = useMemo<BackgroundCheckDispositionFormValues>(
    () => ({
      status: toWritableStatus(backgroundCheck?.status),
      screenedOn: toScreenedOnPrefill(backgroundCheck?.startDate),
      note: ""
    }),
    [backgroundCheck?.status, backgroundCheck?.startDate]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<BackgroundCheckDispositionFormValues>({
    mode: "onChange",
    defaultValues
  });

  const [upsertBackgroundCheck, { loading: mutationLoading }] = useMutation(UPSERT_PERSON_BACKGROUND_CHECK_MUTATION, {
    refetchQueries: [{ query: GET_PROSPECT_PROGRAM_QUERY, variables: { pgaId } }],
    awaitRefetchQueries: true
  });

  const saving = isSubmitting || mutationLoading || !!confirmStatus;
  const statusValue = watch("status");
  const screenedOnValue = watch("screenedOn");
  const expirationHelper = statusValue === "APPROVED" && screenedOnValue ? addOneYearIsoDate(screenedOnValue) : null;
  const showExpiredHelper = isExpiredDisplayStatus(backgroundCheck?.status);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setBannerError("");
      setConfirmStatus(null);
    }
  }, [open, defaultValues, reset]);

  async function persist(values: BackgroundCheckDispositionFormValues) {
    if (!values.status || !values.screenedOn) return;

    setBannerError("");
    try {
      const { data } = await upsertBackgroundCheck({
        variables: {
          input: {
            pgaId,
            status: values.status,
            screenedOn: values.screenedOn,
            ...(values.note.trim() ? { note: values.note.trim() } : {})
          }
        }
      });

      const payload = data?.upsertPersonBackgroundCheck;
      if (payload?.success) {
        showSnackbar(payload.message || "Background check updated");
        setConfirmStatus(null);
        onClose();
        return;
      }

      setBannerError(payload?.message || "Could not update background check.");
      setConfirmStatus(null);
    } catch (error) {
      setBannerError((error as Error).message || "Could not update background check.");
      setConfirmStatus(null);
    }
  }

  function onSave(values: BackgroundCheckDispositionFormValues) {
    if (!values.status) return;
    if (requiresDispositionConfirm(values.status)) {
      setConfirmStatus(values.status);
      return;
    }
    void persist(values);
  }

  function handleClosed() {
    if (saving) return;
    setConfirmStatus(null);
    onClose();
  }

  const selectOptions = WRITABLE_STATUS_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label
  }));

  return (
    <>
      <Dialog
        className="p-6 w-[480px]"
        open={open && !confirmStatus}
        onClosed={handleClosed}
        headline={EDIT_BACKGROUND_CHECK_LABEL}
        displayOverflow
        hasFormElements
        disableClose={saving}
        actions={
          <>
            <Button variant="text" onClick={handleClosed} disabled={saving} testId="bgc-disposition-cancel">
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSave)} disabled={saving} testId="bgc-disposition-save">
              Save
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-6" data-testid="bgc-disposition-modal">
          {!!bannerError && <Banner inline variant="error" description={bannerError} />}

          {showExpiredHelper && (
            <Banner
              inline
              variant="info"
              description="This check is expired. Set Status to Approved and enter a new screened date to renew."
            />
          )}

          <Controller
            name="status"
            control={control}
            rules={{ required: "Status is required" }}
            render={({ field: { onChange, value } }) => (
              <Select
                label="Status"
                required
                placeholder="Select status"
                options={selectOptions}
                value={value}
                onSelect={onChange}
                dropdownPlacement="above"
                error={!!errors.status}
                errorText={errors.status?.message}
                data-testid="bgc-disposition-status"
              />
            )}
          />

          <Controller
            name="screenedOn"
            control={control}
            rules={{
              required: "Screened date is required",
              validate: (value) => {
                if (!value) return "Screened date is required";
                if (isFutureIsoDate(value)) return "Screened date cannot be in the future.";
                return true;
              }
            }}
            render={({ field: { onChange, value } }) => (
              <DateSelect
                label="Screened date"
                useSingleDate
                useMultiCalendar={false}
                useModal
                value={formatDateForDateSelect(value)}
                maxDate={new Date()}
                onChange={(next) => onChange(dateSelectToIso(next))}
                error={!!errors.screenedOn}
                errorMessage={errors.screenedOn?.message}
                supportingText={
                  expirationHelper ? `Expiration is one year after the screened date (${expirationHelper}).` : undefined
                }
                allowClear={false}
              />
            )}
          />

          <Controller
            name="note"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                type="textarea"
                rows={4}
                label="Note"
                value={value || ""}
                onChangeText={onChange}
                className="w-full"
              />
            )}
          />
        </div>
      </Dialog>

      <Dialog
        className="p-6 w-[400px]"
        open={!!confirmStatus}
        onClosed={() => {
          if (!mutationLoading) setConfirmStatus(null);
        }}
        headline={confirmStatus === "DENIED" ? "Confirm Denied" : "Confirm Approved"}
        disableClose={mutationLoading}
        actions={
          <>
            <Button
              variant="text"
              onClick={() => setConfirmStatus(null)}
              disabled={mutationLoading}
              testId="bgc-disposition-confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              theme={confirmStatus === "DENIED" ? "error" : undefined}
              disabled={mutationLoading}
              onClick={handleSubmit(persist)}
              testId="bgc-disposition-confirm-save"
            >
              Confirm
            </Button>
          </>
        }
      >
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          {confirmStatus === "DENIED"
            ? "Mark this background check as Denied? This updates the official membership record."
            : "Mark this background check as Approved? Expiration will be one year from the screened date."}
        </p>
      </Dialog>
    </>
  );
}
